// @ts-nocheck
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import Truck from "@insite/mobius/Icons/Truck";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
// import React, { FC, useEffect } from "react"; // Starter Custom Code
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

/* Starter Custom Code */
import { Dictionary } from "@insite/client-framework/Common/Types";
import getColor from "@insite/mobius/utilities/getColor";
import HeaderLink, { HeaderLinkStyles } from "@store/Library/Components/Toolkit/HeaderLink";
import CarFill, { CarFillOffset } from "@store/Library/Components/Toolkit/Icons/Car/CarFill";
import ShoppingBagFill, {
    ShoppingBagFillOffset,
} from "@store/Library/Components/Toolkit/Icons/ShoppingBag/ShoppingBagFill";
import ShoppingCartFill, {
    ShoppingCartFillOffset,
} from "@store/Library/Components/Toolkit/Icons/ShoppingCart/ShoppingCartFill";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { IconMap } from "@toolkit/Framework/Types/Icon";
import React, { FC, useEffect, useMemo } from "react";
// import { headerHideForDesktopStyles } from "@store/Themes/common/mixins/header";
/* Starter Custom Code */

// Foundation --->
import { linkStyles } from "@store/Themes/common/mixins/link";
import { useIsSignedInAndhasToBeSignedIn } from "@store/Framework/Store/Context/ContextHooks";
// <--- Foundation

/* Store Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ShoppingCartLine from "@store/Library/Components/Toolkit/Icons/ShoppingCart/ShoppingCartLine";
/* Store Custom Code - End */

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: ApplicationState) => ({
    shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
    totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
});

const mapDispatchToProps = {
    loadCurrentCart,
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
    // Store Custom Code
    cartLinkTextWrapper?: InjectableCss;
    cartLabel?: TypographyPresentationProps;
}

export const cartLinkStyles: CartLinkStyles = {
    routerLink: {
        icon: {
            iconProps: {
                color: "inherit", // Store Custom Code
                size: 28,
                css: css`
                    position: relative;
                    margin-right: 0;
                `,
            },
        },
        css: css`
            ${linkStyles("text.darkest", "primary", false, false)}; /* Store Custom Code */
            white-space: nowrap; /* Foundation */
            /* Store Custom Code - Start */
            :hover {
                *::after {
                    background-color: ${getColor("primary")};
                }
            /* Store Custom Code - End */
        `,
        typographyProps: {
            css: css`
                ${({ theme }) => `
                font-size: ${theme.design.typography.size.smaller};
                font-weight: ${theme.design.typography.weight.bold};
                `}
            `,
        },
    },
    compactCountText: {
        css: css`
            ${({ theme }) => `
                font-size: ${theme.design.typography.size.smaller};
                font-weight: ${theme.design.typography.weight.bold};
            `}
        `,
    },
    /* Store Custom Code - Start */
    cartLinkTextWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-left: ${pxToRem(8)};
        `,
    },
    cartLabel: {
        css: css`
            font-size: ${({ theme }) => theme.design.typography.size.smallest};
            line-height: ${({ theme }) => theme.design.typography.lineHeight.smaller};
            color: ${getColor("text.accent")};
            font-weight: ${({ theme }) => theme.design.typography.weight.base};
        `,
    },
    /* Store Custom Code - End */
};

const iconMap = {
    // Foundation --->
    cart: ShoppingCartLine, // Store Custom Code
    truck: CarFill,
    bag: ShoppingBagFill,
    none: undefined,
    // <--- Foundation
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay = 0; // Starter Custom Code

const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon },
    loadCurrentCart,
    isCompactHeaderCart,
    theme,
}) => {
    const isSignedInAndhasToBeSignedIn = useIsSignedInAndhasToBeSignedIn(); // Foundation

    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false });
        }
    }, [shouldLoadCart]);

    const styles = useMergeStyles("cartLink", cartLinkStyles);

    const showIcon = visibilityState !== "label";
    const showLabel = visibilityState !== "icon";

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const icon = iconMap[(showIcon && selectedIcon) || "none"];

    const { routerLink } = styles;

    // Foundation --->
    const iconPropsWithItems = {
        ...routerLink.icon.iconProps,
        css: css`
            ${routerLink.icon.iconProps.css}
            :hover::after {
                background-color: ${getColor("primary")};
            }
            &::after {
                content: "";
                position: absolute;
                right: ${pxToRem(-1)};
                top: ${pxToRem(2)};
                display: block;
                width: 8px;
                height: 8px;
                background-color: ${getColor("secondary")};
                border-radius: 50%;
            }
        `,
    };
    // <--- Foundation

    if (isCompactHeaderCart) {
        return (
            <Link
                {...routerLink}
                color={theme.header.linkColor}
                href={cartUrl}
                icon={{
                    ...routerLink?.icon,
                    iconProps: {
                        src: icon,
                        ...(retainedTotalCountToDisplay && retainedTotalCountToDisplay > 0
                            ? iconPropsWithItems
                            : routerLink?.icon.iconProps) /* Foundation */,
                    },
                }}
                data-test-selector="cartLink"
            >
                <Typography as="span" {...styles.compactCountText}>
                    {retainedTotalCountToDisplay}
                </Typography>
                <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
            </Link>
        );
    }

    // Foundation --->
    if (!isSignedInAndhasToBeSignedIn) {
        return null;
    }
    // <--- Foundation

    return (
        <Link
            {...routerLink}
            href={cartUrl}
            icon={{
                ...routerLink?.icon,
                iconProps: {
                    src: icon,
                    ...(retainedTotalCountToDisplay && retainedTotalCountToDisplay > 0
                        ? iconPropsWithItems
                        : routerLink?.icon.iconProps) /* Foundation */,
                },
            }}
            data-test-selector="cartLink"
        >
            <Hidden below="lg" as="span">
                {/* Store Custom Code - Start */}
                <StyledWrapper {...styles.cartLinkTextWrapper}>
                    {showLabel && <Typography {...styles.cartLabel}>{translate("My Cart")}</Typography>}
                    <Typography as="span" {...styles.desktopCountText} data-test-selector="cartLinkQuantity">
                        {`${retainedTotalCountToDisplay} ${
                            retainedTotalCountToDisplay > 1 ? translate("items") : translate("item")
                        }`}
                    </Typography>
                </StyledWrapper>
                {/* Store Custom Code - End */}
            </Hidden>
            <Hidden above="md">
                <Typography as="span" {...styles.mobileCountText}>
                    {retainedTotalCountToDisplay}
                </Typography>
            </Hidden>
            <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
        </Link>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartLink)),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
